import React, { useEffect, useState, useCallback } from "react";
import { adminApi, ADMIN_API_BASE, getAdminToken } from "@/lib/adminApi";
import { Search, Download, ShieldCheck, ShieldOff, Zap, Ban, Trash2, Filter, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
    const [q, setQ] = useState("");
    const [city, setCity] = useState("");
    const [verified, setVerified] = useState("");
    const [premium, setPremium] = useState("");
    const [banned, setBanned] = useState("");
    const [hasPhotos, setHasPhotos] = useState("");
    const [sort, setSort] = useState("newest");
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [total, setTotal] = useState(0);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { page, limit, sort };
        if (q) params.q = q;
        if (city) params.city = city;
        if (verified !== "") params.verified = verified === "true";
        if (premium !== "") params.premium = premium === "true";
        if (banned !== "") params.banned = banned === "true";
        if (hasPhotos !== "") params.has_photos = hasPhotos === "true";
        try {
            const { data } = await adminApi.get("/admin/users", { params });
            setUsers(data.users || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error("Couldn't load users");
        } finally { setLoading(false); }
    }, [q, city, verified, premium, banned, hasPhotos, sort, page, limit]);

    useEffect(() => { load(); }, [load]);

    const doAction = async (userId, action, extra = {}) => {
        try {
            await adminApi.post(`/admin/users/${userId}/action`, { action, ...extra });
            toast.success(`${action} done`);
            load();
            setSelected(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const exportCsv = () => {
        const token = getAdminToken();
        fetch(`${ADMIN_API_BASE}/admin/users/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `flatch-users-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
            });
    };

    const totalPages = Math.max(1, Math.ceil(total / limit));

    return (
        <div data-testid="admin-users-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-black">Users</h1>
                    <p className="text-sm text-white/50">{total.toLocaleString()} total</p>
                </div>
                <button
                    onClick={exportCsv}
                    data-testid="admin-users-export-btn"
                    className="h-9 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 text-xs font-semibold flex items-center gap-2"
                >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
            </div>

            {/* Filters */}
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        placeholder="Search name or email…"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        data-testid="admin-users-search"
                        className="w-full h-9 pl-9 pr-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-[#E85D5D]"
                    />
                </div>
                <input placeholder="City" value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }}
                    className="h-9 w-28 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
                <SelectFilter label="Verified" value={verified} onChange={(v) => { setVerified(v); setPage(1); }} />
                <SelectFilter label="Premium" value={premium} onChange={(v) => { setPremium(v); setPage(1); }} />
                <SelectFilter label="Banned" value={banned} onChange={(v) => { setBanned(v); setPage(1); }} />
                <SelectFilter label="Photos" value={hasPhotos} onChange={(v) => { setHasPhotos(v); setPage(1); }} />
                <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none"
                >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="email_asc">Email A-Z</option>
                </select>
            </div>

            {/* Table */}
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] font-black uppercase tracking-widest text-white/50 border-b border-white/10">
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">City</th>
                                <th className="px-4 py-3">Budget</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Joined</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-users-table">
                            {loading ? (
                                <tr><td colSpan={6} className="text-center text-white/50 py-8">Loading…</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan={6} className="text-center text-white/50 py-8">No users match</td></tr>
                            ) : users.map((u) => (
                                <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`admin-user-row-${u.user_id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className="h-8 w-8 rounded-full bg-[#E85D5D]/20 text-[#E85D5D] grid place-items-center text-xs font-black">
                                                {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold flex items-center gap-1">
                                                    {u.name || "—"}
                                                    {u.verified && <ShieldCheck className="h-3 w-3 text-[#3B82F6]" fill="currentColor" strokeWidth={0} />}
                                                </div>
                                                <div className="text-xs text-white/50 truncate">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-white/70">{u.city || "—"}</td>
                                    <td className="px-4 py-3 text-white/70">
                                        {u.budget_min || u.budget_max ? `₹${u.budget_min || 0}–${u.budget_max || 0}` : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1 flex-wrap">
                                            {u.premium_until && new Date(u.premium_until) > new Date() && (
                                                <span className="text-[9px] font-black uppercase tracking-widest bg-[#A855F7]/20 text-[#A855F7] rounded-full px-2 py-0.5">premium</span>
                                            )}
                                            {u.banned && (
                                                <span className="text-[9px] font-black uppercase tracking-widest bg-[#EF4444]/20 text-[#EF4444] rounded-full px-2 py-0.5">banned</span>
                                            )}
                                            {!u.vibe_done && (
                                                <span className="text-[9px] font-black uppercase tracking-widest bg-white/10 text-white/50 rounded-full px-2 py-0.5">incomplete</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-white/50 text-xs">{(u.created_at || "").slice(0, 10)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => setSelected(u)}
                                            data-testid={`admin-user-open-${u.user_id}`}
                                            className="text-xs text-[#E85D5D] font-black uppercase tracking-widest hover:underline"
                                        >
                                            manage
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between p-3 border-t border-white/10">
                    <div className="text-xs text-white/50">Page {page} / {totalPages}</div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                            className="h-8 w-8 rounded-lg border border-white/10 grid place-items-center disabled:opacity-40 hover:bg-white/5">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                            className="h-8 w-8 rounded-lg border border-white/10 grid place-items-center disabled:opacity-40 hover:bg-white/5">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            {selected && (
                <UserDetailDrawer user={selected} onClose={() => setSelected(null)} onAction={doAction} />
            )}
        </div>
    );
}

function SelectFilter({ label, value, onChange }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none"
        >
            <option value="">{label}: any</option>
            <option value="true">{label}: yes</option>
            <option value="false">{label}: no</option>
        </select>
    );
}

function UserDetailDrawer({ user, onClose, onAction }) {
    const [confirm, setConfirm] = useState(null);
    const [banReason, setBanReason] = useState("");
    const [giftDays, setGiftDays] = useState(30);
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex" onClick={onClose} data-testid="admin-user-drawer">
            <div className="ml-auto h-full w-full max-w-md bg-[#0B0B0F] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#E85D5D]">user</div>
                        <div className="font-display text-2xl font-black">{user.name || "—"}</div>
                        <div className="text-sm text-white/60">{user.email}</div>
                    </div>
                    <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-white/5 grid place-items-center">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="px-5 pb-5 space-y-4">
                    <InfoRow label="User ID" value={user.user_id} />
                    <InfoRow label="Age / Gender" value={`${user.age || "—"} · ${user.gender || "—"}`} />
                    <InfoRow label="City" value={user.city || "—"} />
                    <InfoRow label="Budget" value={user.budget_min || user.budget_max ? `₹${user.budget_min || 0}–${user.budget_max || 0}` : "—"} />
                    <InfoRow label="Photos" value={String(user.photos)} />
                    <InfoRow label="Vibe quiz" value={user.vibe_done ? "done" : "pending"} />
                    <InfoRow label="Verified" value={user.verified ? "yes ✓" : "no"} />
                    <InfoRow label="Premium until" value={user.premium_until || "—"} />
                    <InfoRow label="Banned" value={user.banned ? `yes — ${user.banned_reason || ""}` : "no"} />
                    <InfoRow label="Auth" value={user.auth_provider} />
                    <InfoRow label="Joined" value={user.created_at?.slice(0, 16)} />

                    <div className="pt-3 border-t border-white/10 space-y-2">
                        <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Actions</div>
                        <div className="grid grid-cols-2 gap-2">
                            {!user.verified ? (
                                <ActionBtn testId="admin-verify-btn" onClick={() => onAction(user.user_id, "verify")} icon={ShieldCheck} label="Verify" color="#3B82F6" />
                            ) : (
                                <ActionBtn testId="admin-unverify-btn" onClick={() => onAction(user.user_id, "unverify")} icon={ShieldOff} label="Unverify" color="#64748B" />
                            )}
                            <ActionBtn testId="admin-gift-btn" onClick={() => setConfirm("gift")} icon={Zap} label="Gift premium" color="#A855F7" />
                            {!user.banned ? (
                                <ActionBtn testId="admin-ban-btn" onClick={() => setConfirm("ban")} icon={Ban} label="Ban" color="#EF4444" />
                            ) : (
                                <ActionBtn testId="admin-unban-btn" onClick={() => onAction(user.user_id, "unban")} icon={Ban} label="Unban" color="#10B981" />
                            )}
                            <ActionBtn testId="admin-delete-btn" onClick={() => setConfirm("delete")} icon={Trash2} label="Delete" color="#EF4444" />
                        </div>
                    </div>

                    {confirm === "gift" && (
                        <div className="rounded-xl border border-[#A855F7]/40 bg-[#A855F7]/10 p-3">
                            <label className="text-[10px] font-black uppercase tracking-widest">Days to gift</label>
                            <input type="number" value={giftDays} onChange={(e) => setGiftDays(parseInt(e.target.value) || 30)}
                                className="mt-1 h-9 w-full rounded-lg bg-white/5 border border-white/10 px-3 text-sm outline-none" />
                            <div className="mt-2 flex gap-2">
                                <button onClick={() => setConfirm(null)} className="flex-1 h-9 rounded-lg border border-white/10 text-sm">Cancel</button>
                                <button onClick={() => onAction(user.user_id, "gift_premium", { days: giftDays })} data-testid="admin-gift-confirm" className="flex-1 h-9 rounded-lg bg-[#A855F7] text-white font-black text-sm">Gift</button>
                            </div>
                        </div>
                    )}
                    {confirm === "ban" && (
                        <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 p-3">
                            <label className="text-[10px] font-black uppercase tracking-widest">Ban reason</label>
                            <input value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder="Reason (optional)"
                                className="mt-1 h-9 w-full rounded-lg bg-white/5 border border-white/10 px-3 text-sm outline-none" />
                            <div className="mt-2 flex gap-2">
                                <button onClick={() => setConfirm(null)} className="flex-1 h-9 rounded-lg border border-white/10 text-sm">Cancel</button>
                                <button onClick={() => onAction(user.user_id, "ban", { reason: banReason })} data-testid="admin-ban-confirm" className="flex-1 h-9 rounded-lg bg-[#EF4444] text-white font-black text-sm">Confirm ban</button>
                            </div>
                        </div>
                    )}
                    {confirm === "delete" && (
                        <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 p-3">
                            <div className="text-sm font-semibold">Permanently delete this user and all their data?</div>
                            <div className="text-xs text-white/60 mt-1">This removes swipes, matches, messages, notifications, listings and contracts. Cannot be undone.</div>
                            <div className="mt-2 flex gap-2">
                                <button onClick={() => setConfirm(null)} className="flex-1 h-9 rounded-lg border border-white/10 text-sm">Cancel</button>
                                <button onClick={() => onAction(user.user_id, "delete")} data-testid="admin-delete-confirm" className="flex-1 h-9 rounded-lg bg-[#EF4444] text-white font-black text-sm">Delete permanently</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50">{label}</div>
            <div className="text-sm text-right break-all">{value}</div>
        </div>
    );
}

function ActionBtn({ onClick, icon: Icon, label, color, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="h-11 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold flex items-center gap-2 justify-center transition"
            style={{ color }}
        >
            <Icon className="h-4 w-4" strokeWidth={2.5} /> {label}
        </button>
    );
}
